import React, { useEffect, useState } from 'react';
import {
  dateFormat,
  PaperProps,
  requiredField,
  dateFnsFormat,
  characterLimit,
  startDateError,
  timeBlockSuccess,
} from 'constants';
import {
  onSave,
  currentDate,
  refinePayload,
  useNotification,
  fieldValidation,
  getDialogHeading,
  maxDateValidation,
  formatTimeZoneDate,
  dropDownValidation,
  showBackdropLoader,
  renderSaveCancelButtons,
} from 'utils/commonUtils';
import * as yup from 'yup';
import moment from 'moment';
import { format } from 'date-fns';
import MDBox from 'components/MDBox';
import Grid from '@mui/material/Grid';
import { useForm } from 'react-hook-form';
import Dialog from '@mui/material/Dialog';
import { useDispatch } from 'react-redux';
import { GridForm } from 'components/Ui/styled';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormInputText } from 'components/Common/Forms/FormTextField';
import { FormInputDate } from 'components/Common/Forms/FormInputDate';
import FormInputDropdown from 'components/Common/Forms/FormInputDropDown';
import {
  putSubjectTimeBlock,
  postSubjectTimeBlock,
} from 'layouts/tutoring/services/tutoringApi';

const subjectsAndTurorsSchema = yup.object().shape({
  subject: dropDownValidation,
  reason: fieldValidation(characterLimit),
  startsAt: yup.date().required(requiredField).typeError(requiredField),
  endsAt: maxDateValidation('startsAt', startDateError),
});

const ManageTimeBlocksDialog = (props) => {
  const {
    open,
    setOpen,
    subjects,
    newTimeBlock,
    isFormLoading,
    timeBlockForm,
    setNewTimeBlock,
    setTimeBlockForm,
    fetchTutoringSubjects,
    fetchSubjectTimeBlocks,
  } = props;

  const [loadingButton, setLoadingButton] = useState(false);
  const { reason, subjectId, startsAt, endsAt, timeBlockId } =
    timeBlockForm || {};

  const defaultValue = {
    reason: reason || '',
    subject: subjectId || '',
    startsAt: startsAt ? format(new Date(startsAt), dateFnsFormat) : '',
    endsAt: endsAt ? format(new Date(endsAt), dateFnsFormat) : '',
  };

  const { setOpenNotification, setNotificationMessage, NotificationPopup } =
    useNotification();
  const dispatch = useDispatch();

  const useFunction = useForm({
    mode: 'onChange',
    defaultValues: defaultValue,
    resolver: yupResolver(subjectsAndTurorsSchema),
  });

  const { handleSubmit, reset, control, formState, watch } = useFunction;
  const { errors } = formState;

  const watchedStartsAt = watch('startsAt');
  const watchedEndsAt = watch('endsAt');

  const handleClose = () => {
    setOpen(false);
    reset(defaultValue);
    setTimeBlockForm(null);
    setNewTimeBlock(false);
  };

  useEffect(() => {
    reset(defaultValue);
  }, [
    defaultValue.reason,
    defaultValue.endsAt,
    defaultValue.subject,
    defaultValue.startsAt,
  ]);

  const onSubmit = async (data) => {
    const { reason, subject, startsAt, endsAt } = data;

    const payload = {
      reason: reason,
      subjectId: subject,
      endsAt: moment(endsAt).format(dateFormat),
      startsAt: moment(startsAt).format(dateFormat),
    };

    const fetchApi = () => {
      fetchTutoringSubjects(subjectId);
      if (subjectId) {
        fetchSubjectTimeBlocks();
      }
    };

    onSave(
      timeBlockSuccess,
      newTimeBlock,
      postSubjectTimeBlock,
      putSubjectTimeBlock,
      refinePayload(payload),
      timeBlockId,
      fetchApi,
      handleClose,
      setOpenNotification,
      setNotificationMessage,
      dispatch,
      setLoadingButton
    );
  };

  const subjectOptions = subjects?.map((item) => ({
    id: item.subjectId,
    value: item.name,
  }));

  return (
    <>
      {showBackdropLoader(isFormLoading)}
      <Dialog open={open} PaperProps={PaperProps}>
        {getDialogHeading('Time Block', handleClose)}

        <GridForm container spacing={2}>
          <Grid item xs={12}>
            <FormInputDropdown
              name="subject"
              type="text"
              control={control}
              options={subjectOptions}
              label="Subject"
              helperText={errors?.subject?.message}
              errors={!!errors.subject}
              disabled={!newTimeBlock}
            />
          </Grid>
          <Grid item xs={12}>
            <FormInputText
              name="reason"
              type="text"
              control={control}
              required={true}
              label="Reason"
              helperText={errors?.reason?.message}
              errors={!!errors.reason}
            />
          </Grid>
          <Grid item xs={12}>
            <FormInputDate
              name="startsAt"
              control={control}
              label="Starts At"
              maxDate={watchedEndsAt}
              helperText={errors?.startsAt?.message}
              errors={!!errors.startsAt}
              value={startsAt}
            />
          </Grid>
          <Grid item xs={12}>
            <MDBox mt={1}></MDBox>
            <FormInputDate
              name="endsAt"
              control={control}
              label="Ends At"
              minDate={watchedStartsAt}
              helperText={errors?.endsAt?.message}
              errors={!!errors.endsAt}
              value={endsAt}
            />
          </Grid>
          {renderSaveCancelButtons(
            handleClose,
            handleSubmit(onSubmit),
            loadingButton
          )}
        </GridForm>
        <NotificationPopup />
      </Dialog>
    </>
  );
};

export default ManageTimeBlocksDialog;
